import React, { useEffect, useMemo, useState } from 'react';
import { Modal, ModalHandle, toArray } from 'Mri.Web.Common';
import { ActiveDisplay } from '../Displays/ActiveDisplay';
import { observer } from 'mobx-react';
import { useStore } from '../hooks/use-store';
import { ServiceKit } from './ServiceKitStore';
import { Button, Collapse, FormGroup, Input, ListGroup, ListGroupItem, Tooltip } from 'reactstrap';
import Fuse from 'fuse.js';
import classNames from 'classnames';
import { BsFillExclamationTriangleFill } from '@react-icons/all-files/bs/BsFillExclamationTriangleFill';

interface props {
    modalHandle: ModalHandle;
    display: ActiveDisplay;
    serviceKits: ServiceKit[];
    onClose?(): void;
}

export const EditDisplayServiceKitsModal = observer((props: props) => {
    const { modalHandle, display, serviceKits } = props;
    const { serviceKitStore, displaysStore } = useStore();
    const [initialSetupComplete, setInitialSetupComplete] = useState(false);
    const [availableServiceKits, setAvailableServiceKits] = useState([] as ServiceKit[]);
    const [filteredServiceKits, setFilteredServiceKits] = useState([] as ServiceKit[]);
    const [selectedServiceKits, setSelectedServiceKits] = useState([] as any);
    const [currentlyAssociatedServiceKits, setCurrentlyAssociatedServiceKits] = useState([] as ServiceKit[]);
    const [currentlyAssociatedServiceKitsMap, setCurrentlyAssociatedServiceKitsMap] = useState([] as any);
    const [loadingAssociated, setLoadingAssociated] = useState(false);
    const [loadingAvailable, setLoadingAvailable] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [bulkEditIsOpen, setBulkEditIsOpen] = useState(false);
    const [bulkEditTooltipOpen, setBulkEditTooltipOpen] = useState(false);
    const [serviceKitToDelete, setServiceKitToDelete] = useState({} as ServiceKit);
    const fuse = new Fuse([] as any, {
        keys: ['name', 'description'],
    });
    const confirmDeleteModalHandle = useMemo(() => new ModalHandle(), []);

    useEffect(() => {
        // if initial setup hasn't been run yet, run it
        if (!initialSetupComplete) {
            initialSetup();
            return;
        }
        // if the modal is open and the confirm delete modal is not open, run initial setup
        if (!confirmDeleteModalHandle.show || modalHandle.show) initialSetup();
    }, [modalHandle.show, confirmDeleteModalHandle.show]);

    // Reset to the default state when the modal is closed
    useEffect(() => {
        if (modalHandle.show) return;
        reset();
    }, [modalHandle.show]);

    useEffect(() => {
        filterServiceKits();
    }, [availableServiceKits, selectedServiceKits, currentlyAssociatedServiceKitsMap]);

    useEffect(() => {
        setCurrentlyAssociatedServiceKits(displaysStore.activeDisplayServiceKitsList);
    }, [displaysStore.activeDisplayServiceKitsList]);

    const initialSetup = async () => {
        loadAllServiceKits();
        loadAssociatedServiceKits();
        filterServiceKits();
        setInitialSetupComplete(true);
    };

    const loadAllServiceKits = () => {
        serviceKitStore
            .loadAllServiceKits()
            .then(() => {
                setAvailableServiceKits(toArray(serviceKitStore.serviceKits));
            })
            .catch((error: any) => console.error(error))
            .finally(() => {
                // filterServiceKits();
                setLoadingAvailable(false);
            });
    };
    const loadAssociatedServiceKits = () => {
        setCurrentlyAssociatedServiceKits(props.serviceKits as ServiceKit[]);
        setCurrentlyAssociatedServiceKitsMap(
            props.serviceKits.map((serviceKit: ServiceKit) => serviceKit.serviceKitGuid)
        );
    };
    const reset = () => {
        setFilteredServiceKits([]);
        setSelectedServiceKits([]);
        setSearchTerm('');
        // props.onClose();
    };
    const filterServiceKits = () => {
        const toRemove = [...selectedServiceKits, ...currentlyAssociatedServiceKitsMap];
        const filtered = availableServiceKits.filter(serviceKit => {
            return !toRemove // filtering out the selected and currently associated service kits
                .includes(serviceKit.serviceKitGuid);
        });
        setFilteredServiceKits(filtered);
        fuse.setCollection(filtered); // updating the fuse collection to the filtered list so as to not search for selected items
    };

    const search = (text: string) => {
        setSearchTerm(text);
        filterServiceKits();
        if (text === '') return;
        const results = fuse.search(text);
        setFilteredServiceKits(results.map(result => result.item) as ServiceKit[]);
    };

    const submitAsync = async () => {
        await displaysStore.addServiceKitsToDisplay(display.serialNumber, selectedServiceKits as string[]);
        modalHandle.close();
    };

    const deleteServiceKit = async () => {
        await displaysStore.removeServiceKitFromDisplay(
            display.serialNumber,
            serviceKitToDelete.serviceKitGuid as string
        );
        setCurrentlyAssociatedServiceKitsMap(
            currentlyAssociatedServiceKitsMap.filter(
                (serviceKitGuid: string) => serviceKitGuid !== serviceKitToDelete.serviceKitGuid
            )
        );
        setServiceKitToDelete({} as ServiceKit);
        confirmDeleteModalHandle.close();
    };

    const toggleBulkAddTooltip = () => setBulkEditTooltipOpen(!bulkEditTooltipOpen);

    const Footer = () => (
        <div className="w-100 d-flex justify-content-around">
            <div className="w-100 d-flex justify-content-end">
                <Button color={'secondary'} className={'mr-3'} onClick={modalHandle.close}>
                    Cancel
                </Button>
                <Button type="submit" color={'primary'} onClick={submitAsync}>
                    Save
                </Button>
            </div>
        </div>
    );
    const ConfirmDeleteModalFooter = () => (
        <>
            <div className="confirm-footer">
                <Button
                    color={'secondary'}
                    onClick={() => {
                        setServiceKitToDelete({} as ServiceKit);
                        confirmDeleteModalHandle.close();
                    }}
                >
                    Cancel
                </Button>
                <Button color={'danger'} onClick={deleteServiceKit}>
                    Delete
                </Button>
            </div>
        </>
    );
    const ConfirmDeleteModal = ({ serviceKit, handle }: { serviceKit: ServiceKit; handle: ModalHandle }) => (
        <Modal modalHandle={handle} className={classNames('p-0')} size={'sm'} footer={ConfirmDeleteModalFooter()}>
            <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
            </div>
            <div className="modal-body">
                <div>Are you sure you want to remove the {serviceKit.name} service kit from this display?</div>
            </div>
        </Modal>
    );

    return (
        <>
            <Modal
                modalHandle={modalHandle}
                size={'md'}
                title={`Edit Service Kits for display: ${display.serialNumber}`}
                footer={Footer()}
            >
                <ConfirmDeleteModal serviceKit={serviceKitToDelete} handle={confirmDeleteModalHandle} />
                <h5>Current Service Kits</h5>
                <ListGroup flush className={'mb-4'}>
                    {loadingAssociated && loadingAvailable && <ListGroupItem>Loading...</ListGroupItem>}
                    {!loadingAssociated &&
                        !loadingAvailable &&
                        displaysStore.activeDisplayServiceKitsList.length === 0 && (
                        <ListGroupItem>No Service Kits Currently Associated</ListGroupItem>
                    )}
                    {!loadingAssociated &&
                        !loadingAvailable &&
                        displaysStore.activeDisplayServiceKitsList.length !== 0 &&
                        displaysStore.activeDisplayServiceKitsList.map(serviceKit => {
                            return (
                                <ListGroupItem key={`associated-service-kit-${serviceKit.serviceKitGuid}`}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="name">{serviceKit.name}</div>
                                        <Button
                                            color={'danger'}
                                            size={'sm'}
                                            onClick={() => {
                                                setServiceKitToDelete(serviceKit);
                                                confirmDeleteModalHandle.open();
                                            }}
                                        >
                                            Delete
                                        </Button>
                                    </div>
                                </ListGroupItem>
                            );
                        })}
                </ListGroup>

                <div className="d-flex justify-content-start align-items-center mb-4">
                    <Button onClick={() => setBulkEditIsOpen(!bulkEditIsOpen)} className={'mr-1'}>
                        {!bulkEditIsOpen && 'Bulk Add Service Kits'}
                        {bulkEditIsOpen && 'Hide Bulk Add Service Kits'}
                    </Button>
                    <BsFillExclamationTriangleFill
                        id={'bulk-edit-tooltip-icon'}
                        style={{ marginLeft: '0.5rem' }}
                        color={'yellow'}
                    />
                    <Tooltip
                        placement={'right'}
                        target={'bulk-edit-tooltip-icon'}
                        toggle={() => toggleBulkAddTooltip()}
                    >
                        <span>
                            In order to preserve service kit info relevant to this display, Service Kits can only be
                            added in bulk, and must be removed one at a time.
                        </span>
                    </Tooltip>
                </div>
                <Collapse isOpen={bulkEditIsOpen}>
                    <h5>Selected Service Kits</h5>
                    <ListGroup flush className={'mb-4'}>
                        {loadingAssociated && loadingAvailable && <ListGroupItem>Loading...</ListGroupItem>}
                        {!loadingAssociated && !loadingAvailable && selectedServiceKits.length === 0 && (
                            <ListGroupItem>No Service Kits Selected</ListGroupItem>
                        )}
                        {!loadingAssociated &&
                            !loadingAvailable &&
                            selectedServiceKits.length > 0 &&
                            availableServiceKits.map((serviceKit, index) => {
                                if (!selectedServiceKits.includes(serviceKit.serviceKitGuid)) return;
                                return (
                                    <ListGroupItem
                                        key={`selected-${index}`}
                                        className={'d-flex justify-content-between'}
                                        onClick={() =>
                                            setSelectedServiceKits(
                                                selectedServiceKits.filter(
                                                    (selectedServiceKit: any) =>
                                                        selectedServiceKit !== serviceKit.serviceKitGuid
                                                )
                                            )
                                        }
                                    >
                                        <span>{serviceKit.name}</span>
                                        <Button color={'danger'}>-</Button>
                                    </ListGroupItem>
                                );
                            })}
                    </ListGroup>
                    <h5>Available Service Kits</h5>
                    <FormGroup>
                        <label htmlFor="#search-service-kits">Search</label>
                        <Input
                            id="search-service-kits"
                            type="text"
                            value={searchTerm}
                            onChange={e => search(e.target.value)}
                        />
                    </FormGroup>
                    <ListGroup flush>
                        {loadingAvailable && <ListGroupItem>Loading...</ListGroupItem>}
                        {!loadingAvailable && filteredServiceKits.length === 0 && (
                            <ListGroupItem>No Service Kits Available</ListGroupItem>
                        )}
                        {!loadingAvailable &&
                            filteredServiceKits.length > 0 &&
                            filteredServiceKits.map((serviceKit, index) => {
                                return (
                                    <ListGroupItem
                                        key={`available-${index}`}
                                        onClick={() =>
                                            setSelectedServiceKits([...selectedServiceKits, serviceKit.serviceKitGuid])
                                        }
                                        className={'d-flex justify-content-between'}
                                    >
                                        <span>{serviceKit.name}</span>
                                        <Button color={'primary'}>+</Button>
                                    </ListGroupItem>
                                );
                            })}
                    </ListGroup>
                </Collapse>
            </Modal>
        </>
    );
});
